import { useMediaQuery } from '@hooks/useMediaQuery';
import { sha1 } from '@lib/hash';
import { unitStrict } from '@lib/style/unit';
import { hashColor, readableColor } from '@lib/style/utils';
import Avatar from '@material-ui/core/Avatar';
import Icon, { IconProps } from '@material-ui/core/Icon';
import { User } from '@model/User';
import { ImageObject } from '@packages/image/lib/types';
import { createMemo } from '@packages/react';
import { buildVariants } from '@packages/react/lib/buildVariants';
import { IF } from '@packages/react/lib/if';
import { initials } from '@packages/strings';
import { get } from 'lodash';
import React, { CSSProperties, HTMLAttributes, ReactElement, useEffect, useRef, useState } from 'react';
import { InlineFlex } from './Layout/Blocks';
import { TopLoader } from './TopLoader';

type VariantObject = {
    image: null | string;
    text: null | string;
    icon: ReactElement<IconProps, typeof Icon>;
};

const LOADER_PERCENT = 0.55;
const TEXT_PERCENT = 0.43;

// noinspection JSUnusedLocalSymbols
const VARIANTS_OBJECT = {
    user: (props: any): VariantObject => {
        const user$ = new User(props.data);

        return props.data
            ? {
                  image: user$.thumbnail || props.image || null,
                  text: user$.$fullName() || props.text || null,
                  icon: props.icon || <Icon>person</Icon>,
              }
            : VARIANTS.none(props);
    },

    default: (props: any): VariantObject => {
        if (!props.data) {
            return props;
        }

        if (!User.$instance(props.data)) {
            return props.data;
        }

        return VARIANTS.user(props.data);
    },

    none: (_props: any): VariantObject => ({
        image: null,
        text: null,
        icon: <Icon>broken_image</Icon>,
    }),
};

const VARIANTS = buildVariants(VARIANTS_OBJECT);

interface ProfilePictureProps extends HTMLAttributes<HTMLElement> {
    variant?: keyof typeof VARIANTS;
    data?: any;
    image?: string | ImageObject;
    text?: string;
    icon?: any;

    size?: number;
    radius?: any;
    backgroundColor?: any;
}

export const ProfilePicture = createMemo<ProfilePictureProps>(
    (props) => {
        const { size, variant, data: __data, image, text, icon, style, radius, backgroundColor, ...rest } = props;
        const isMobile = useMediaQuery('(max-width: 959px)');
        const size$ = parseInt(`${!isMobile ? size || '48' : size || '40'}`);
        const imageRef = useRef<HTMLImageElement | null>(null);

        const setImageDisplay = (value: string): void => {
            if (imageRef.current) {
                imageRef.current.style.display = value;
            }
        };

        const [avatarProps, setAvatarProps] = useState<{ [key: string]: any }>({});
        const [data, setData] = useState<VariantObject>(VARIANTS.getVariant(variant)(props));
        const [loading, setLoading] = useState<boolean>(!!data.image);

        const data$ = props.data || {};
        const uniqData = sha1(
            (User.$instance(data$)
                ? [data$.id, data$.$fullName(), data$.thumbnail, variant]
                : [data$.image || props.image, data$.text || props.text, variant]
            ).join('_'),
        );

        useEffect(() => {
            // Update children from variant when keyed data has been changed
            setData(VARIANTS.getVariant(variant)(props));
        }, [uniqData]);

        useEffect(() => {
            // Empty avatar props
            const avatarProps$: any = {};

            // If data comes with image
            if (data.image) {
                // set source
                avatarProps$.src =
                    typeof data.image === 'object' ? (data.image as ImageObject).thumbnail_path : data.image;

                // If image it's different than old one
                if (avatarProps$.src !== avatarProps.src) {
                    setImageDisplay('none');
                    setLoading(true);
                }

                // Setup avatar image
                avatarProps$.alt = data.text || 'Profile picture';
                avatarProps$.imgProps = {
                    ref: imageRef,
                    style: { display: 'none' },

                    onError: () => {
                        // Hide image element
                        setImageDisplay('none');
                        setLoading(false);

                        // refresh component without image (try to render with text or icon)
                        setData({
                            ...data,
                            image: null,
                        });
                    },

                    onLoad: () => {
                        setImageDisplay('inherit');
                        setLoading(false);
                    },
                };
            } else if (data.text) {
                // if there's text show nice initials eg: Custom Name -> CN
                avatarProps$.children = `${initials(data.text)}`.toUpperCase();
                avatarProps$.title = data.text;
            } else {
                // if nothing above match, try to render icon
                avatarProps$.children = data.icon || null;
            }

            // get children component name
            const childrenDisplayName = get(avatarProps$, 'children.type.displayName');

            // if there's no image on the new data
            // clean old ref, hide loader
            if (!data.image) {
                imageRef.current = null;
                setLoading(false);
            }

            // Set avatar size (WxH)
            avatarProps$.style = {
                ...(style || {}),
                width: size$,
                height: size$,
            };

            // Force GPU render (to remove glitches)
            avatarProps$.style.transform = 'translate3d(0, 0, 0)';
            if (radius) {
                avatarProps$.style.borderRadius = unitStrict(radius);
            }

            // set font size by size ratio
            avatarProps$.style.fontSize = avatarProps$.style.fontSize
                ? avatarProps$.style.fontSize
                : Math.floor(size$ * TEXT_PERCENT);

            if (childrenDisplayName && /icon/i.test(childrenDisplayName)) {
                avatarProps$.children = {
                    ...avatarProps$.children,
                    props: {
                        ...avatarProps$.children.props,
                        style: {
                            ...(avatarProps$.children.props.style || {}),
                            fontSize: avatarProps$.style.fontSize,
                        },
                    },
                };
            }

            // set font weight
            avatarProps$.style.fontWeight = avatarProps$.style.fontWeight ? avatarProps$.style.fontWeight : 500;

            if (typeof avatarProps$.children === 'string') {
                // Generate a nice unique background and color by string for our text letters
                avatarProps$.style.backgroundColor = backgroundColor ?? hashColor(avatarProps$.children);
                avatarProps$.style.color = readableColor(avatarProps$.style.backgroundColor);
            }

            // Update avatar props just if new state it's different than old state
            if (sha1(avatarProps) !== sha1(avatarProps$)) {
                setAvatarProps({ ...avatarProps$ });
            }
        }, [data.image, data.text, isMobile, get(data, 'icon.props.children')]);

        // Force GPU render
        const style$: CSSProperties = {
            transform: 'translate3d(0, 0, 0)',
        };

        // Change cursor to pointer if there's a click event
        if (rest.onClick) {
            style$.cursor = 'pointer';
        }

        return (
            <InlineFlex
                w={size$}
                h={size$}
                minWidth={size$}
                minHeight={size$}
                r={radius ?? size$ / 2}
                overflow={'hidden'}
                style={style$}
                {...rest}
            >
                <Avatar {...avatarProps} />
                {IF(
                    !!avatarProps.src,
                    <TopLoader
                        show={loading}
                        size={Math.floor(size$ * LOADER_PERCENT)}
                        p={0}
                        t={0}
                        b={0}
                        r={0}
                        l={0}
                        z={3}
                        style={{ borderRadius: size$ / 2 }}
                    />,
                )}
            </InlineFlex>
        );
    },
    {
        displayName: 'ProfilePicture',
        defaultProps: {
            variant: VARIANTS.getDefaultVariant(),
            size: 48,
            icon: <Icon>broken_image</Icon>,
        },
    },
);
